import { formatRevenueCents } from '@/lib/analytics/channel-format';

/**
 * The honest sentence for "what did this post sell".
 *
 * ## Why this is a module and not two template literals in the card
 *
 * The differentiator here is provenance, not the number. Triple Whale sells one
 * blended figure; Mora says which orders are DETERMINISTIC (a buyer typed a code
 * Mora created — Shopify records it on the order, nothing had to be tracked) and
 * which are inferred from where a visit came from. That distinction is worth
 * nothing if the two clauses can disagree, and they can disagree the moment they
 * are written inline in a component with an `&&` between them.
 *
 * ## The rules
 *
 *  · `total` is the UNION of the two, never the sum. An order can be both — the
 *    buyer tapped the bio link AND typed the code — and `post_attribution_summary`
 *    already resolves it as code-matched. This module never re-adds them.
 *  · A zero is never rendered as a number. "0 orders · $0" reads as broken; "No
 *    sales have used this code yet" reads as an answer. Every degenerate case
 *    here returns words.
 *  · Revenue may be honest-NULL — an attributed order whose revenue has not
 *    synced is still attribution. `formatRevenueCents` renders that as an
 *    em-dash, so the revenue clause is dropped entirely rather than showing one.
 *
 * Voice is pinned to `WhosBuyingSection.tsx:114-118`.
 */

export interface ProvenanceInput {
  codeMatched: number;
  tokenMatched: number;
  total: number;
  totalRevenueCents: number | null;
}

export interface ProvenanceCopy {
  /** The fact. "4 orders · $286 from this post." — or null when there are none. */
  headline: string | null;
  /** How those orders were matched. Null when there is nothing to explain. */
  provenance: string | null;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Builds both sentences. `headline` is null when nothing has sold, which is the
 * signal for the caller to render the "no sales yet" state rather than a zero.
 */
export function buildProvenanceCopy(input: ProvenanceInput): ProvenanceCopy {
  const { codeMatched, tokenMatched, total, totalRevenueCents } = input;

  if (total <= 0) {
    return { headline: null, provenance: null };
  }

  const money = formatRevenueCents(totalRevenueCents);
  const orders = `${total} ${plural(total, 'order', 'orders')}`;
  const headline =
    money === '—' ? `${orders} from this post.` : `${orders} · ${money} from this post.`;

  // Both mechanisms contributed — the case the whole card exists to make legible.
  if (codeMatched > 0 && tokenMatched > 0) {
    return {
      headline,
      provenance: `${codeMatched} matched to a code Mora created, ${tokenMatched} matched by where the visit came from.`,
    };
  }

  if (codeMatched > 0) {
    return {
      headline,
      provenance: `${codeMatched === total ? 'All' : String(codeMatched)} matched to a code Mora created — Shopify recorded the code on the order, so nothing had to be tracked.`,
    };
  }

  if (tokenMatched > 0) {
    return {
      headline,
      provenance: `${tokenMatched === total ? 'All' : String(tokenMatched)} matched by where the visit came from — no code was used, so this is inferred from the link, not recorded at checkout.`,
    };
  }

  return { headline, provenance: null };
}

/**
 * "No sales have used this code yet. Checked 2 minutes ago." — the E5 evidence
 * that work actually ran. Without the timestamp the user cannot tell a check
 * from a spinner that resolved into the same screen.
 */
export function buildCheckedSentence(lastCheckedAt: string | null, now: Date = new Date()): string {
  if (!lastCheckedAt) return 'Not checked yet.';
  const then = new Date(lastCheckedAt).getTime();
  if (!Number.isFinite(then)) return 'Not checked yet.';

  const seconds = Math.max(0, Math.round((now.getTime() - then) / 1000));
  if (seconds < 60) return 'Checked just now.';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `Checked ${minutes} ${plural(minutes, 'minute', 'minutes')} ago.`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Checked ${hours} ${plural(hours, 'hour', 'hours')} ago.`;
  const days = Math.round(hours / 24);
  return `Checked ${days} ${plural(days, 'day', 'days')} ago.`;
}

/**
 * The bio-link tap line. Says "taps", never "people": link-preview crawlers are
 * classified and excluded, but user-agent bot detection is best-effort and a tap
 * is the only thing actually observed.
 */
export function buildTapSentence(
  clicks: number,
  token: string | null,
  topReferrerHost: string | null,
): string {
  if (clicks <= 0) return 'Nobody has tapped your bio link yet.';
  const count = `${clicks} ${plural(clicks, 'tap', 'taps')}`;
  const tag = token ? `, tagged ${token}` : '';
  const from = topReferrerHost ? `, mostly from ${topReferrerHost}` : '';
  return `${count} on your bio link${from}${tag}.`;
}
